import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Global logout handler that can be set by AuthContext 1
let globalLogoutHandler: (() => void) | null = null;

export const setGlobalLogoutHandler = (handler: () => void) => {
  globalLogoutHandler = handler;
};

// Track last activity time for idle timeout
let lastActivityTime = Date.now();
const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 1 minute in milliseconds (change to 15 for production)

// Don't track activity via events - we'll update it only after successful idle checks
// This prevents the activity time from being updated during the check itself

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

console.log('API instance created with baseURL:', API_BASE_URL);

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Add auth token to requests and check idle timeout
console.log('Registering request interceptor');
api.interceptors.request.use((config) => {
  console.log('Request interceptor - making request to:', config.url);
  const token = localStorage.getItem('token');

  // Skip idle timeout check for auth endpoints (login, refresh, forgot-password, reset-password)
  const isAuthEndpoint = config.url?.includes('/auth/login') ||
                         config.url?.includes('/auth/refresh') ||
                         config.url?.includes('/auth/forgot-password') ||
                         config.url?.includes('/auth/reset-password');

  // For auth endpoints, don't add token or check idle time - let them proceed normally
  if (isAuthEndpoint) {
    return config;
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;

    // Check if user has been idle for too long
    const currentTime = Date.now();
    const idleTime = currentTime - lastActivityTime;

    if (idleTime > IDLE_TIMEOUT_MS) {
      // User has been idle for too long, force logout
      console.log('Idle timeout exceeded! Logging out...');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      lastActivityTime = Date.now(); // Reset activity time

      if (globalLogoutHandler) {
        try {
          globalLogoutHandler();
        } catch (logoutError) {
          console.error('Error during logout:', logoutError);
        }
      }

      toast.error('Session expired due to inactivity. Please login again.');

      // The globalLogoutHandler already cleared user state, React Router will handle redirect
      return Promise.reject(new Error('Session expired due to inactivity'));
    }

    // User is active, update last activity time for this request
    lastActivityTime = currentTime;
  }

  return config;
});

// Handle auth errors and token refresh
console.log('Registering response interceptor');
api.interceptors.response.use(
  (response) => {
    console.log('Response interceptor - successful response:', response.config.url, 'Status:', response.status);
    return response;
  },
  async (error: AxiosError) => {
    console.log('=== Response interceptor triggered ===');
    console.log('Error status:', error.response?.status);
    console.log('Error detail:', (error.response?.data as any)?.detail);

    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Check if this is a 401 error and not already a retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('401 error detected, not a retry');

      // Check if the backend returned "Session expired due to inactivity"
      const errorDetail = (error.response?.data as any)?.detail;
      const isIdleTimeout = errorDetail === 'Session expired due to inactivity';
      console.log('Is idle timeout:', isIdleTimeout);

      // Check if idle timeout has been exceeded (client-side or server-side)
      const idleTime = Date.now() - lastActivityTime;
      console.log('Idle time check:', idleTime, 'ms');

      if (idleTime > IDLE_TIMEOUT_MS || isIdleTimeout) {
        console.log('Idle timeout exceeded, forcing logout');
        // User has been idle for too long, force logout
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        lastActivityTime = Date.now(); // Reset activity time

        if (globalLogoutHandler) {
          try {
            globalLogoutHandler();
          } catch (logoutError) {
            console.error('Error during logout:', logoutError);
          }
        }

        toast.error('Session expired due to inactivity. Please login again.');

        // The globalLogoutHandler already cleared user state, React Router will handle redirect
        return Promise.reject(error);
      }

      // If we're already refreshing, queue this request
      if (isRefreshing) {
        console.log('Already refreshing, queueing this request');
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      console.log('Starting token refresh process');
      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      console.log('Refresh token available:', !!refreshToken);

      if (!refreshToken) {
        console.log('No refresh token found, forcing logout');
        // No refresh token available, force logout
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        lastActivityTime = Date.now(); // Reset activity time

        if (globalLogoutHandler) {
          try {
            globalLogoutHandler();
          } catch (logoutError) {
            console.error('Error during logout:', logoutError);
          }
        }

        toast.error('Your session has expired. Please login again.');

        // The globalLogoutHandler already cleared user state, React Router will handle redirect
        return Promise.reject(error);
      }

      try {
        console.log('Calling refresh endpoint...');
        // Attempt to refresh the token
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          { refresh_token: refreshToken },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        console.log('Refresh successful!');
        const { access_token, refresh_token: newRefreshToken } = response.data;

        // Update tokens in localStorage
        localStorage.setItem('token', access_token);
        localStorage.setItem('refreshToken', newRefreshToken);

        // Update the Authorization header
        originalRequest.headers.Authorization = `Bearer ${access_token}`;

        // Update last activity time since we successfully refreshed
        lastActivityTime = Date.now();
        console.log('Updated activity time after refresh');

        // Process the queued requests
        processQueue(null, access_token);

        isRefreshing = false;

        console.log('Retrying original request...');
        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        console.log('Refresh failed:', refreshError);
        // Refresh failed, force logout
        processQueue(refreshError as AxiosError, null);
        isRefreshing = false;

        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        lastActivityTime = Date.now(); // Reset activity time

        if (globalLogoutHandler) {
          try {
            globalLogoutHandler();
          } catch (logoutError) {
            console.error('Error during logout:', logoutError);
          }
        }

        toast.error('Your session has expired. Please login again.');

        // The globalLogoutHandler already cleared user state, React Router will handle redirect
        return Promise.reject(refreshError);
      }
    }

    console.log('Not a 401 or already retried, rejecting error');
    return Promise.reject(error);
  }
);

export default api;